import { endOfMonth, format, startOfMonth } from 'date-fns';
import { z } from 'zod';

import { FILTER_FIELDS, OPERATORS, getFilterField } from './filterFields';

import {
  VisionMonitorFilter,
  VisionMonitorOperator,
  VisionMonitorScope,
} from '@/services/VisionMonitorService';

export const ALL_CUSTOMERS = '0';

const columnFilterSchema = z
  .object({
    field: z.string().min(1, 'Choose a column'),
    operator: z.string().min(1),
    value: z.string(),
    values: z.array(z.string()),
  })
  .superRefine((filter, ctx) => {
    const definition = getFilterField(filter.field);
    if (!definition) return;

    const operator = OPERATORS[definition.type].find(
      (item) => item.value === filter.operator,
    );
    if (!operator?.needsValue) return;

    if (definition.type === 'enum') {
      if (filter.values.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['values'],
          message: 'Pick at least one option',
        });
      }
      return;
    }

    if (filter.value.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: 'Required',
      });
      return;
    }

    if (
      definition.type === 'number' &&
      !Number.isFinite(Number(filter.value))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: 'Must be a number',
      });
    }
  });

const dateField = z.string().regex(/^(\d{4}-\d{2}-\d{2})?$/, 'Invalid date');

export const filtersFormSchema = z
  .object({
    customerId: z.string(),
    createdFrom: dateField,
    createdTo: dateField,
    releaseFrom: dateField,
    releaseTo: dateField,
    filters: z.array(columnFilterSchema).max(20),
  })
  .superRefine((form, ctx) => {
    const ranges = [
      ['createdFrom', 'createdTo'],
      ['releaseFrom', 'releaseTo'],
    ] as const;

    ranges.forEach(([from, to]) => {
      if (form[from] && form[to] && form[from] > form[to]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [to],
          message: 'Must be after the start date',
        });
      }
    });
  });

export type FiltersFormValues = z.infer<typeof filtersFormSchema>;

// The page always opens on the current month, by creation date.
export function getCurrentMonthScope(): VisionMonitorScope {
  const today = new Date();

  return {
    createdFrom: format(startOfMonth(today), 'yyyy-MM-dd'),
    createdTo: format(endOfMonth(today), 'yyyy-MM-dd'),
  };
}

export function scopeToFormValues(
  scope: VisionMonitorScope,
): FiltersFormValues {
  return {
    customerId: scope.customerId ? String(scope.customerId) : ALL_CUSTOMERS,
    createdFrom: scope.createdFrom ?? '',
    createdTo: scope.createdTo ?? '',
    releaseFrom: scope.releaseFrom ?? '',
    releaseTo: scope.releaseTo ?? '',
    filters: (scope.filters ?? []).map((filter) => ({
      field: filter.field,
      operator: filter.operator,
      value: Array.isArray(filter.value) ? '' : (filter.value ?? ''),
      values: Array.isArray(filter.value) ? filter.value : [],
    })),
  };
}

export function formValuesToScope(form: FiltersFormValues): VisionMonitorScope {
  const filters = form.filters
    .filter((filter) =>
      FILTER_FIELDS.some((item) => item.field === filter.field),
    )
    .map((filter): VisionMonitorFilter => {
      const definition = getFilterField(filter.field)!;
      const needsValue = OPERATORS[definition.type].find(
        (item) => item.value === filter.operator,
      )?.needsValue;

      const base = {
        field: filter.field,
        operator: filter.operator as VisionMonitorOperator,
      };

      if (!needsValue) return base;

      return {
        ...base,
        value: definition.type === 'enum' ? filter.values : filter.value.trim(),
      };
    });

  return {
    ...(form.customerId !== ALL_CUSTOMERS && {
      customerId: Number(form.customerId),
    }),
    ...(form.createdFrom && { createdFrom: form.createdFrom }),
    ...(form.createdTo && { createdTo: form.createdTo }),
    ...(form.releaseFrom && { releaseFrom: form.releaseFrom }),
    ...(form.releaseTo && { releaseTo: form.releaseTo }),
    ...(filters.length > 0 && { filters }),
  };
}
